import React, { useState, useEffect } from "react";
import api from "../../../utils/api";
import { BASE_URL, studentsApi } from "../../../api/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EditIcon, Save, Key } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../utils/AuthContext";

const UpdateStudent = () => {
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
        created_at: "",
        status: "1",
        role: 1,
    });

    const [isEditName, setIsEditName] = useState(false);
    const [isChangePassword, setIsChangePassword] = useState(false);
    const { user, updateUser } = useAuth();

    useEffect(() => {
        if (user) {
            setFormData((prev) => ({
                ...prev,
                id: user.id,
                name: user.name || "",
                phone: user.phone || "",
                email: user.email || "",
                role: 1,
                status: user.status?.toString() || "1",
                created_at: user.created_at || new Date().toISOString(),
            }));
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "profile_picture") {
            setFormData((prev) => ({
                ...prev,
                profile_picture: files[0],
                profile_picture_url: URL.createObjectURL(files[0]),
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            formData.password &&
            formData.password !== formData.confirmPassword
        ) {
            toast.error("Passwords do not match");
            return;
        }

        try {
            const data = new FormData();
            data.append("name", formData.name);
            data.append("role", 1);
            data.append("status", 1);
            data.append("created_at", formData.created_at);
            if (formData.password) {
                data.append("password", formData.password);
                data.append("password_confirmation", formData.confirmPassword);
            }

            await api.post(
                `${BASE_URL}${studentsApi}/${formData.id}?_method=PUT`,
                data,
                {
                    headers: {
                        Accept: "application/json",
                    },
                },
            );

            updateUser({ ...user, name: formData.name });
            toast.success("Student updated successfully!");
            setIsEditName(false); // hide button after update
        } catch (error) {
            console.error(error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach((msg) =>
                    toast.error(msg[0]),
                );
            } else {
                toast.error("Error updating student");
            }
        }
    };

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <div className="flex items-center justify-center h-full">
            <div className="w-full max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                {/* Standardized Editorial Header */}
                <div className="mb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-2 ">
                            Account{" "}
                            <span className="text-yellow-500">
                                Information.
                            </span>
                        </h2>
                        <p className="text-gray-500 text-sm md:text-base font-medium">
                            Manage your personal details and account
                            preferences.
                        </p>
                    </motion.div>
                </div>

                {/* Form Container */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="bg-white p-6 md:p-8 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden"
                >
                    {/* Background Watermark Element (Optional, matches theme) */}
                    <div className="absolute -top-10 -right-10 text-[150px] text-gray-50 opacity-50 font-black pointer-events-none select-none z-0">
                        @
                    </div>

                    <form onSubmit={handleSubmit} className="relative z-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                            {/* Name Field */}
                            <motion.div
                                variants={itemVariants}
                                className="flex flex-col gap-2"
                            >
                                <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide">
                                    Name
                                </label>
                                <div className="relative flex items-center">
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter student name"
                                        required
                                        disabled={!isEditName}
                                        className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 outline-none text-neutral-900 font-medium
                                        ${
                                            isEditName
                                                ? "border-neutral-900 bg-white focus:ring-2 focus:ring-yellow-400/30 pr-12 shadow-sm"
                                                : "border-gray-200 bg-gray-50 text-gray-500 pr-12"
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsEditName((prev) => !prev)
                                        }
                                        className={`absolute cursor-pointer right-3 p-2 rounded-md transition-colors flex items-center justify-center
                                        ${
                                            isEditName
                                                ? "bg-yellow-400 text-neutral-900 hover:bg-yellow-500"
                                                : "text-gray-400 hover:text-neutral-900 hover:bg-gray-200"
                                        }`}
                                        aria-label="Edit Name"
                                    >
                                        <EditIcon size={16} />
                                    </button>
                                </div>
                            </motion.div>

                            {/* Phone Field */}
                            <motion.div
                                variants={itemVariants}
                                className="flex flex-col gap-2"
                            >
                                <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Enter phone number"
                                    pattern="[0-9]{10}"
                                    readOnly
                                    disabled
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 font-medium outline-none"
                                />
                            </motion.div>

                            {/* Email Field */}
                            <motion.div
                                variants={itemVariants}
                                className="flex flex-col gap-2 md:col-span-2"
                            >
                                <label className="text-sm font-bold text-neutral-900 uppercase tracking-wide">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter email address"
                                    readOnly
                                    disabled
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 font-medium outline-none lowercase"
                                />
                            </motion.div>
                        </div>

                        {/* Animated Submit & Action Buttons */}
                        <AnimatePresence>
                            {isEditName && (
                                <motion.div
                                    initial={{
                                        opacity: 0,
                                        height: 0,
                                        marginTop: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        height: "auto",
                                        marginTop: 32,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        height: 0,
                                        marginTop: 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden border-t border-gray-100 pt-6 mt-8 flex flex-col sm:flex-row justify-between items-center gap-4"
                                >
                                    {/* Save Changes Button */}
                                    <button
                                        type="submit"
                                        className="flex cursor-pointer items-center justify-center gap-2 px-8 py-3 bg-neutral-900 text-white rounded-lg font-bold hover:bg-yellow-400 hover:text-neutral-900 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 w-full sm:w-auto"
                                    >
                                        <Save size={18} />
                                        Save Changes
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                            {/* Change Password Button */}
                            You can change your password by clicking the button below.
                            <a
                                className="flex cursor-pointer items-center justify-center gap-2 px-8 py-3 bg-neutral-900 text-white rounded-lg font-bold hover:bg-yellow-400 hover:text-neutral-900 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 w-full sm:w-auto text-center"
                                href="/change-password"
                            >
                                <Key size={18} />
                                Change Password
                            </a>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default UpdateStudent;
